import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { LogIn, FileText, Search } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const handle = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/");
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">AOMS</h1>
          <p className="text-sm text-muted-foreground mt-1">BDM Order Management System</p>
        </div>
        <Card className="p-6 md:p-8">
          <h2 className="font-display font-semibold text-lg mb-1">Sign in to your account</h2>
          <p className="text-sm text-muted-foreground mb-6">Use your assigned credentials</p>
          <form onSubmit={handle} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full" data-testid="login-submit-btn">
              {loading ? "Signing in..." : (<><LogIn className="w-4 h-4 mr-2" /> Sign in</>)}
            </Button>
          </form>
        </Card>

        <div className="mt-6 space-y-3">
          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">or</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Link to="/b2b/new" className="w-full" data-testid="b2b-billing-link">
              <Button variant="outline" className="w-full">
                <FileText className="w-4 h-4 mr-2" /> B2B Billing
              </Button>
            </Link>
            <Link to="/b2b/track" className="w-full" data-testid="b2b-track-link">
              <Button variant="outline" className="w-full">
                <Search className="w-4 h-4 mr-2" /> Track by Reference
              </Button>
            </Link>
          </div>
          <p className="text-center text-xs text-muted-foreground pt-1">
            New here?{" "}
            <Link to="/ibo/register" className="text-primary hover:underline font-medium" data-testid="ibo-register-link">
              Register as an IBO
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
